import React, { useEffect } from 'react';
import { Box, Button, Card, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  ViewOrderDetailsData,
  DownloadOrderPDF,
  DownloadCreditInvoicePDF,
} from 'reactRedux/thunks/orderThunk/orderThunk';
import PageLoader from 'components/wholePageLoader/PageLoader';
import moment from 'moment';
import {
  rentalStatusMap,
  orderRentalStatus,
  orderPaymentStatus,
  paymentStatusMap,
  paymentStatusColors,
} from 'utils/StaticData';
import { formatCurrency } from 'utils/CommonFunction';
import { FaFilePdf } from 'react-icons/fa';
import BackButton from 'components/backButton/BackButton';

const OrderDetail = () => {
  const { ordersDetailId } = useParams();
  const dispatch = useDispatch();
  const {
    viewOrderDetails,
    isViewLoading,
    isDownloadLoading,
    isCreditInvoiceDownloadLoading,
  } = useSelector((state) => state.OrderSlice);

  useEffect(() => {
    if (ordersDetailId) {
      dispatch(ViewOrderDetailsData({ id: ordersDetailId }));
    }
  }, [ordersDetailId, dispatch]);

  const handleInvoiceGeneration = () => {
    dispatch(DownloadOrderPDF({ id: ordersDetailId }));
  };

  const handleCreditInvoiceGeneration = () => {
    dispatch(DownloadCreditInvoicePDF({ id: ordersDetailId }));
  };

  return (
    <>
      {isViewLoading ? (
        <PageLoader />
      ) : (
        <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
          <Flex direction="column" align="center" justify="center">
            {viewOrderDetails ? (
              <>
                <Flex
                  align="center"
                  justify="space-between"
                  flexWrap={'wrap'}
                  width="100%"
                  gap={3}
                  pb={3}
                >
                  <BackButton
                    fallbackUrl="/admin/orders"
                    label="Back to Orders"
                  />
                  <Flex gap={3} flexWrap="wrap">
                    {viewOrderDetails?.intentId &&
                      ![orderRentalStatus.LOST].includes(
                        viewOrderDetails?.rentalStatus,
                      ) && (
                        <Button
                          type="button"
                          bg="primary"
                          color="white"
                          w={{ base: '100%', md: 'auto' }}
                          _hover={{ bg: 'primary', opacity: 0.8 }}
                          disabled={isDownloadLoading}
                          isLoading={isDownloadLoading}
                          loadingText="Generating..."
                          fontSize="sm"
                          fontWeight="500"
                          px={4}
                          flexShrink={0}
                          justifyContent={{ sm: 'start', md: 'center' }}
                          leftIcon={<FaFilePdf size={25} />}
                          onClick={() => {
                            handleInvoiceGeneration();
                          }}
                        >
                          Invoice
                        </Button>
                      )}

                    {viewOrderDetails?.intentId &&
                      [orderPaymentStatus.REFUNDED].includes(
                        viewOrderDetails?.paymentStatus,
                      ) && (
                        <Button
                          type="button"
                          bg="primary"
                          color="white"
                          w={{ base: '100%', md: 'auto' }}
                          _hover={{ bg: 'primary', opacity: 0.8 }}
                          disabled={isCreditInvoiceDownloadLoading}
                          isLoading={isCreditInvoiceDownloadLoading}
                          loadingText="Generating..."
                          fontSize="sm"
                          fontWeight="500"
                          px={4}
                          flexShrink={0}
                          leftIcon={<FaFilePdf size={25} />}
                          justifyContent={{ sm: 'start', md: 'center' }}
                          onClick={() => {
                            handleCreditInvoiceGeneration();
                          }}
                        >
                          Credit Invoice
                        </Button>
                      )}
                  </Flex>
                </Flex>

                <Card
                  w="100%"
                  boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                  borderRadius="20px"
                  paddingInline="40px"
                  paddingBlock="40px"
                  overflowX={{ sm: 'scroll', lg: 'hidden' }}
                >
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Order ID:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.orderId}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Intent ID:
                      </Text>

                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.intentId || '—'}
                      </Text>
                    </Flex>
                    {viewOrderDetails?.usageIntentId && (
                      <Flex
                        alignItems={{ sm: 'start', md: 'center' }}
                        direction={{ sm: 'column', md: 'row' }}
                        gap="8px"
                        padding="4px 8px"
                        borderLeft={'1px solid #ccc'}
                      >
                        <Text fontSize="lg" fontWeight="600">
                          Addl. Charge Intent ID:
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {viewOrderDetails?.usageIntentId}
                        </Text>
                      </Flex>
                    )}
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Payment Type:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.paymentType}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Powerbank ID:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.powerbankId}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Rental Status:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {rentalStatusMap[viewOrderDetails?.rentalStatus] ||
                          'N/A'}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Payment Status:
                      </Text>
                      <Text
                        fontSize="sm"
                        color={
                          paymentStatusColors[
                            viewOrderDetails?.paymentStatus
                          ] || 'gray.500'
                        }
                      >
                        {paymentStatusMap[viewOrderDetails?.paymentStatus] ||
                          'Unknown'}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Rent Time:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {moment
                          .parseZone(viewOrderDetails?.rentTime)
                          .format('DD-MM-YYYY HH:mm:ss')}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Return Time:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.returnTime ? (
                          <Text fontSize="sm" color="gray.600">
                            {moment
                              .parseZone(viewOrderDetails.returnTime)
                              .format('DD-MM-YYYY HH:mm:ss')}
                          </Text>
                        ) : (
                          ''
                        )}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Duration:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.duration}
                      </Text>
                    </Flex>
                    {/* <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Deposit:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {formatCurrency(
                          viewOrderDetails?.deposit,
                          viewOrderDetails?.currency,
                        )}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Refunded Amount:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {formatCurrency(
                          viewOrderDetails?.amountRefunded,
                          viewOrderDetails?.currency,
                        )}
                      </Text>
                    </Flex> */}
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Settlement Amount:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {formatCurrency(
                          viewOrderDetails?.settlementAmount,
                          viewOrderDetails?.currency,
                        )}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Captured Amount:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {formatCurrency(
                          viewOrderDetails?.capturedAmount,
                          viewOrderDetails?.currency,
                        )}
                      </Text>
                    </Flex>

                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Captured Date:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.capturedDate
                          ? moment
                              .parseZone(viewOrderDetails.capturedDate)
                              .format('DD-MM-YYYY HH:mm:ss')
                          : 'N/A'}
                      </Text>
                    </Flex>

                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Settlement Date:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.settlementDate
                          ? moment
                              .parseZone(viewOrderDetails.settlementDate)
                              .format('DD-MM-YYYY HH:mm:ss')
                          : 'N/A'}
                      </Text>
                    </Flex>

                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Rental Merchant:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.rentalMerchant}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Rental Station:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.rentalStationId}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Rental Slot:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.rentalSlot}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Return Merchant:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.returnMerchant}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Return Station:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.returnStationId}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Return Slot:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.returnSlot}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        User:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {viewOrderDetails?.user}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Discount Amount:
                      </Text>
                      <Text fontSize="sm" color="green.600">
                        {formatCurrency(
                          viewOrderDetails?.discountAmount,
                          viewOrderDetails?.currency,
                        )}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Last 4:
                      </Text>
                      <Text fontSize="sm">{viewOrderDetails?.last4}</Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Coupon Id:
                      </Text>
                      <Text fontSize="sm">
                        {viewOrderDetails?.couponId
                          ? viewOrderDetails?.couponId
                          : ''}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="8px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontSize="lg" fontWeight="600">
                        Coupon Code:
                      </Text>
                      <Text fontSize="sm">
                        {viewOrderDetails?.couponCode
                          ? viewOrderDetails?.couponCode
                          : ''}
                      </Text>
                    </Flex>
                  </SimpleGrid>
                </Card>
                {viewOrderDetails?.note && (
                  <Flex
                    alignSelf="start"
                    direction="row"
                    gap="8px"
                    padding="16px 16px"
                    w="100%"
                  >
                    <Text fontSize="lg" fontWeight="600">
                      Note:
                    </Text>
                    <Text fontSize="sm" color="red" marginTop="3px">
                      {viewOrderDetails?.note ? viewOrderDetails?.note : ''}
                    </Text>
                  </Flex>
                )}
              </>
            ) : (
              <Text>No order details found.</Text>
            )}
          </Flex>
        </Box>
      )}
    </>
  );
};

export default OrderDetail;
